import React, { useEffect, useMemo, useState } from "react";
import "./styles.css";
import {
  Anchor, Compass, FlaskConical, ShieldCheck, Droplets, Scale, Sparkles,
  DollarSign, Plug, ArrowRight, Ship, Check, AlertTriangle, Waves,
  Gauge, RefreshCw, Play, ChevronRight, Flame, Beaker,
  Target, Radio, Cpu, Building2, GraduationCap, Award,
  Zap, TrendingUp, Users, Code2, Globe, Network,
} from "lucide-react";
import {
  CARGOES, CARGO, GROUP_COLOR, relation,
  DEFAULT_TANKS, DEFAULT_PARCELS, optimize,
} from "./data";

/* ------------------------------------------------------------------ router */
function useHashRoute() {
  const parse = () => window.location.hash.replace(/^#/, "") || "/";
  const [route, setRoute] = useState(parse());
  useEffect(() => {
    const on = () => { setRoute(parse()); window.scrollTo(0, 0); };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}

const usd = (n) =>
  "$" + Math.round(n).toLocaleString("en-US");

/* -------------------------------------------------------------------- data */
const FEATURES = [
  { icon: Compass,     tag: "OPTIMIZER",  title: "積付シーケンス最適化", body: "CP-SATソルバーが総コスト最小の荷役順序を導出。" },
  { icon: FlaskConical,tag: "CHEMISTRY",  title: "適合性マトリクス",     body: "積荷相互の反応・汚染リスクを自動判定。" },
  { icon: ShieldCheck, tag: "COMPLIANCE", title: "IBCコード検証",       body: "規則違反を計画段階でブロック。" },
  { icon: Droplets,    tag: "CLEANING",   title: "洗浄プランナー",       body: "洗浄要否・グレード・費用を最適化。" },
  { icon: Scale,       tag: "BALLAST",    title: "バラスト／安定性",     body: "喫水・トリムを考慮した配分。" },
  { icon: Sparkles,    tag: "CLEANLINESS",title: "タンク清浄度管理",     body: "直前貨物からの清浄度履歴を追跡。" },
  { icon: DollarSign,  tag: "SAVINGS",    title: "コスト可視化",         body: "洗浄・滞船の削減効果を金額で提示。" },
  { icon: Plug,        tag: "EXPORT",     title: "KC360連携",           body: "既存システムへ計画をデータ出力 (EN/JA)。" },
];

/* --------------------------------------------------------------------- nav */
function Nav({ route }) {
  const links = [
    ["#/", "会社概要"],
    ["#/tankwise", "TankWise"],
    ["#/features", "機能一覧"],
    ["#/demo", "最適化デモ"],
  ];
  return (
    <header className="nav">
      <div className="wrap nav-in">
        <a className="brand" href="#/"><span className="mark"><Target size={17} /></span>Shirushi<span className="brand-ai">.AI</span></a>
        <nav className="nav-links">
          {links.map(([h, l]) => (
            <a key={h} href={h} className={"rlink" + (("#" + route) === h ? " on" : "")}>{l}</a>
          ))}
          <a className="btn btn-primary" href="#/tankwise">プロダクトを見る</a>
        </nav>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------- mini board */
function TankChip({ id, cargoKey, sub }) {
  const c = CARGO[cargoKey];
  return (
    <div className="tk" style={{ background: GROUP_COLOR[c.group] }}>
      {id}<small>{c.ja}</small>{sub ? <em>{sub}</em> : null}
    </div>
  );
}

/* ----------------------------------------------------------- COMPANY HOME */
const PRODUCTS = [
  {
    icon: Radio, domain: "ライブコマース", status: "本番稼働中", live: true, to: null,
    title: "LiveReach",
    sub: "ライブコマース SNS 自動投稿・分析",
    body: "配信中の商品訴求を各SNSへ自動投稿し、視聴者反応・売上をリアルタイムに解析。次の配信の訴求内容と投稿タイミングをAIが最適化。",
    tags: ["マルチSNS自動投稿", "リアルタイム分析", "訴求最適化"],
    cta: "本番稼働中",
  },
  {
    icon: Network, domain: "ギグエコノミー", status: "本番稼働中", live: true, to: null,
    title: "GridPilot",
    sub: "ギグワーク グリッド最適化・配車管理",
    body: "需給をグリッド単位で予測し、ワーカーの配置・配車・稼働を動的に最適化。供給の空白と待機時間を抑え、単位時間あたりの収益を最大化。",
    tags: ["需給予測", "動的配車", "稼働最適化"],
    cta: "本番稼働中",
  },
  {
    icon: Anchor, domain: "海運", status: "開発中", live: false, to: "#/tankwise",
    title: "TankWise",
    sub: "ケミカルタンカー 積付最適化エンジン",
    body: "IBCコード適合性を厳守しながら、洗浄コスト・コンタミリスク・滞船料を同時に最小化する積付シーケンスを、制約ソルバーが数秒で導出。現在、海運パートナーと実証開発中。",
    tags: ["制約プログラミング", "OR-Tools CP-SAT", "海運OEM連携"],
    cta: "デモを見る",
  },
];

const APPROACH = [
  { icon: Users, tag: "伴走", title: "業界の巨人と組む", body: "各産業のリーディングカンパニーと深く伴走し、現場の制約と勝ち筋を理解した上で開発に入ります。" },
  { icon: Cpu, tag: "構築", title: "本番AIプロダクトを構築", body: "研究段階のモデルではなく、実運用に耐えるプロダクトを設計・実装・デプロイまで一気通貫で提供します。" },
  { icon: TrendingUp, tag: "実証", title: "効果を数値で証明", body: "コスト削減・収益向上・時間短縮を定量指標で可視化し、意思決定の根拠として提示します。" },
];

function CompanyHome() {
  return (
    <main className="view">
      <section className="hero">
        <div className="wrap hero-in">
          <div>
            <span className="pill"><span className="dot" />AI ソフトウェア・コンサルティングファーム</span>
            <h1>データの中の<span className="hl">標</span>（しるし）を、<br />意思決定の<span className="hl">武器</span>に。</h1>
            <p className="lede">Shirushi.AI は、各産業の<b>リーディングカンパニー</b>と組み、実運用に耐えるAIプロダクトを構築するコンサルティングファームです。海運・ライブコマース・ギグエコノミー——複雑な制約に満ちた現場を、<b>数理と機械学習</b>で最適化します。</p>
            <div className="cta-row">
              <a className="btn btn-primary" href="#/tankwise">プロダクトを見る<ArrowRight size={16} /></a>
              <a className="btn btn-ghost dark" href="#/demo">最適化デモを体験</a>
            </div>
            <div className="stat-row">
              <div className="stat"><b>3</b><span>産業向けプロダクト</span></div>
              <div className="stat"><b>1年</b><span>創業</span></div>
              <div className="stat"><b>OEM級</b><span>産業ドメインの知見</span></div>
            </div>
          </div>
          <div className="panel co-panel">
            <h4>プロダクト・ポートフォリオ</h4>
            <div className="co-portrows">
              {PRODUCTS.map((p) => (
                <div className="co-portrow" key={p.title}>
                  <span className="co-porticon"><p.icon size={16} /></span>
                  <div>
                    <b>{p.title}</b>
                    <small>{p.sub}</small>
                  </div>
                  <em className={p.live ? "" : "dev"}>{p.status}</em>
                </div>
              ))}
            </div>
            <div className="seqbar" />
            <div className="pfoot">
              <span>産業領域 <b>3</b></span>
              <span>本番稼働 <b className="good">2製品</b></span>
              <span>技術 <b className="warn">AI / 数理最適化</b></span>
            </div>
          </div>
        </div>
      </section>

      <section className="wrap">
        <p className="eyebrow">事業内容</p>
        <h2>研究で終わらせない。現場で動くAIを届ける。</h2>
        <p className="sec-lede">私たちは受託開発会社ではありません。産業の意思決定そのものを設計し直すパートナーです。ドメインの制約を理解し、最適化・機械学習で解き、本番運用まで責任を持ちます。</p>
        <div className="prob">
          {APPROACH.map((a) => (
            <div className="pcard" key={a.tag}>
              <span className="ic"><a.icon size={20} /></span>
              <span className="ftag co-ftag">{a.tag}</span>
              <h3>{a.title}</h3>
              <p>{a.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="feat-band">
        <div className="wrap">
          <p className="eyebrow">プロダクト</p>
          <h2 className="w">3つの産業に、3つのプロダクト。</h2>
          <p className="sec-lede light">それぞれが、その産業でしか通用しない制約を数理で解いています。</p>
          <div className="co-prodgrid">
            {PRODUCTS.map((p) => (
              <div className={"co-prod" + (p.live ? " live" : " dev")} key={p.title}>
                <div className="co-prodhead">
                  <span className="co-prodicon"><p.icon size={22} /></span>
                  <span className="ftag">{p.domain}<i className={"co-status" + (p.live ? " on" : " dev")}>{p.status}</i></span>
                </div>
                <h3>{p.title}</h3>
                <div className="co-prodsub">{p.sub}</div>
                <p>{p.body}</p>
                <ul className="co-prodtags">{p.tags.map((t) => <li key={t}>{t}</li>)}</ul>
                {p.to
                  ? <a className="co-prodcta" href={p.to}>{p.cta} <ArrowRight size={15} /></a>
                  : <span className="co-prodcta soon"><Check size={14} /> {p.cta}</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="wrap">
        <p className="eyebrow">ビジョン</p>
        <div className="co-vision">
          <h2>どの産業にも、まだ数理で解かれていない意思決定が眠っている。</h2>
          <p>熟練者の勘、Excelの手作業、属人化した判断——そこには必ず「標（しるし）」があります。私たちはそれを見つけ、モデル化し、誰もが再現できる最適な意思決定へと変換します。日本の産業が持つ現場力に、世界水準のソフトウェア工学を掛け合わせることが、Shirushi.AI の使命です。</p>
        </div>
      </section>

      <section className="wrap">
        <p className="eyebrow">創業者</p>
        <h2>創業者について</h2>
        <div className="co-profile">
          <div className="co-pcard">
            <div className="co-phead">
              <span className="co-pavatar">DY</span>
              <div>
                <h3>Danniel Sihui Yang</h3>
                <div className="co-prole">創業者 兼 主席エンジニア</div>
              </div>
            </div>
            <p className="co-pbio">大規模な金融・防衛システムを本番運用してきたソフトウェアエンジニア。分散システム、最適化、機械学習パイプラインを軸に、現場の複雑な意思決定を製品化することを専門とします。</p>
            <ul className="co-plist">
              <li><span className="co-plco"><Building2 size={14} /> Citadel</span>Enterprise Data のユーザーツール／グローバル・プライシング基盤をリード。分散(Hot/Hot)アーキテクチャ、C++制約充足エンジンの近代化。</li>
              <li><span className="co-plco"><Building2 size={14} /> Palantir</span>Foundry上のクライアント向けプロダクトを構築。初期の機械学習パイプライン／PySparkデータ基盤を設計。</li>
              <li><span className="co-plco"><Sparkles size={14} /> FantasyKPOP</span>創業者。リアルタイム連携のモバイルゲーム基盤を単独で技術統括。</li>
              <li><span className="co-plco"><GraduationCap size={14} /> 学歴</span>カーネギーメロン大学 修士 (CS, GPA 3.90) ／ トロント大学 学士 (CS, Dean's List)。</li>
              <li><span className="co-plco"><Award size={14} /> 研究</span>ヒューリスティック探索。Pancake問題で既存最良比 7倍 の性能改善 (AAAI/SoCS)。</li>
            </ul>
            <div className="co-plinks">
              <a href="https://www.linkedin.com/in/danniel-yang/" target="_blank" rel="noreferrer"><Globe size={14} /> LinkedIn</a>
              <a href="https://github.com/yangdan4" target="_blank" rel="noreferrer"><Code2 size={14} /> GitHub</a>
            </div>
          </div>
        </div>
      </section>

      <section className="wrap">
        <p className="eyebrow">技術顧問</p>
        <h2>技術顧問について</h2>
        <div className="co-profile">
          <div className="co-pcard">
            <div className="co-phead">
              <span className="co-pavatar adv">龍</span>
              <div>
                <h3>龍 小平 博士 — Ryu Shohei, Ph.D.</h3>
                <div className="co-prole">建設機械技術の第一人者 ／ 電動化・自動化</div>
              </div>
            </div>
            <p className="co-pbio">日立建機 研究本部にて 23年間、次世代建設機械の先行研究と電動化・情報化建設機械の開発を主導。機電分離・移動供電・柔性電網を提唱し、電動建機・電動収穫機を実現。OEM級の建設機械・制御技術で、Shirushi.AI のソフトウェア層を支える確かな土台を提供します。</p>
            <ul className="co-plist">
              <li><span className="co-plco"><Building2 size={14} /> 日立建機</span>研究本部 上級研究員 (1993–2016)。電動化・情報化建設機械の開発を主導。</li>
              <li><span className="co-plco"><Zap size={14} /> 現職</span>杭州藍力電動科技 董事長・首席科学家。電動化・自動化を21世紀最大の産業転換と捉える。</li>
              <li><span className="co-plco"><GraduationCap size={14} /> 学歴</span>埼玉大学 工学博士 ／ 浙江大学 修士 ／ 北京理工大学 学士。</li>
              <li><span className="co-plco"><Award size={14} /> 客座教授</span>浙江大学・西南交通大学 ほか。</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="wrap">
        <div className="cta">
          <p className="eyebrow accent2">お問い合わせ</p>
          <h2 className="w">貴社の意思決定に、標（しるし）を。</h2>
          <p>海運・ライブコマース・ギグエコノミーをはじめ、複雑な制約を抱える産業のAI化をご一緒します。まずは稼働中のプロダクト TankWise の最適化デモをご覧ください。</p>
          <div className="cta-btns">
            <a className="btn btn-primary" href="#/tankwise">TankWise を見る<ArrowRight size={16} /></a>
            <a className="btn btn-ghost dark" href="#/demo">最適化デモを実行</a>
          </div>
        </div>
      </section>
    </main>
  );
}

/* --------------------------------------------------------- TANKWISE (product) */
function Home() {
  return (
    <main className="view">
      <section className="hero">
        <div className="wrap hero-in">
          <div>
            <span className="pill"><span className="dot" />ケミカルタンカー向け 積付最適化エンジン</span>
            <h1>積付の順序を、<span className="hl">経験</span>から<br /><span className="hl">数理</span>へ。</h1>
            <p className="lede">IBCコード適合性を厳守しながら、洗浄コスト・コンタミリスク・滞船料を同時に最小化する最適積付シーケンスを、制約ソルバーが<b>数秒</b>で導出します。</p>
            <div className="cta-row">
              <a className="btn btn-primary" href="#/demo">最適化を体験する<ArrowRight size={16} /></a>
              <a className="btn btn-ghost dark" href="#/features">機能を見る</a>
            </div>
            <div className="stat-row">
              <div className="stat"><b>−28%</b><span>洗浄コスト削減 (想定)</span></div>
              <div className="stat"><b>100%</b><span>IBCコード適合性の担保</span></div>
              <div className="stat"><b>数時間→数秒</b><span>計画作成時間</span></div>
            </div>
          </div>
          <div className="panel">
            <h4>OPTIMIZED STOWAGE PLAN — MT TANKWISE</h4>
            <div className="tanks">
              <TankChip id="1P" cargoKey="ethylene_glycol" />
              <TankChip id="2P" cargoKey="xylene" />
              <TankChip id="3P" cargoKey="methanol" />
              <TankChip id="4P" cargoKey="caustic_soda" />
              <TankChip id="1S" cargoKey="palm_oil" />
              <TankChip id="2S" cargoKey="ethanol" />
              <TankChip id="3S" cargoKey="toluene" />
              <TankChip id="4S" cargoKey="vegetable_oil" />
            </div>
            <div className="seqbar" />
            <div className="pfoot">
              <span>Cleaning: <b>2 grades</b></span>
              <span>Conflicts: <b className="good">0</b></span>
              <span>Demurrage: <b className="warn">Low</b></span>
            </div>
          </div>
        </div>
      </section>

      <section className="wrap">
        <p className="eyebrow">The Problem</p>
        <h2>積付計画は、いまだ「属人的」で「高コスト」。</h2>
        <p className="sec-lede">数十種類の化学品の適合性、洗浄要否、荷役順序、バラスト、船体安定性——絡み合う制約を、少数の熟練者が手作業で解いています。その結果、避けられたはずのコストが積み上がります。</p>
        <div className="prob">
          <div className="pcard"><span className="ic"><Beaker size={20} /></span><h3>コンタミネーション</h3><p>不適合な積み合わせによる品質汚染は、貨物クレームと信頼失墜に直結します。</p></div>
          <div className="pcard"><span className="ic"><Droplets size={20} /></span><h3>過剰な洗浄コスト</h3><p>順序を誤ると不要なタンク洗浄が発生。水・薬剤・時間・スロップ処理費が膨らみます。</p></div>
          <div className="pcard"><span className="ic"><Gauge size={20} /></span><h3>滞船料 (デマレージ)</h3><p>非効率な荷役計画が停泊時間を延ばし、日額の滞船料としてのしかかります。</p></div>
        </div>
      </section>

      <section className="feat-band">
        <div className="wrap">
          <p className="eyebrow">Capabilities</p>
          <h2 className="w">1つのエンジンに、8つの実務モジュール。</h2>
          <p className="sec-lede light">最適化だけでなく、化学・コンプライアンス・洗浄・機器制約まで。現場の意思決定をまるごと支えます。</p>
          <div className="fgrid">
            {FEATURES.map((f) => (
              <div className="fcard" key={f.tag}>
                <f.icon size={22} className="fi" />
                <span className="ftag">{f.tag}</span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 34 }}>
            <a className="btn btn-primary" href="#/demo">最適化デモを実行 <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      <section className="wrap">
        <p className="eyebrow">How it works</p>
        <h2>入力から最適計画まで、3ステップ。</h2>
        <div className="steps">
          <div className="step"><div className="n">1</div><h3>貨物と航路を入力</h3><p>積荷リスト、寄港順、タンク構成、直前貨物を登録します。</p></div>
          <div className="step"><div className="n">2</div><h3>制約ソルバーが求解</h3><p>IBCコード適合性を制約に、コスト最小のシーケンスを整数最適化で導出。</p></div>
          <div className="step"><div className="n">3</div><h3>計画と根拠を出力</h3><p>タンク割当・洗浄計画・削減額を可視化し、KC360形式でエクスポート。</p></div>
        </div>
      </section>

      <section className="wrap">
        <div className="iino">
          <div>
            <p className="eyebrow">For 飯野海運株式会社</p>
            <h2 style={{ marginBottom: 8 }}>貴社の船隊で、効果を実証します。</h2>
            <p className="sec-lede">ステンレスケミカルタンカーを国内外で運航される飯野海運様の実運航データと、当社の最適化エンジンを組み合わせ、洗浄・滞船の削減効果を定量検証するPoCをご提案します。</p>
          </div>
          <ul className="checklist">
            <li><Check size={16} />実際の航路・カーゴパターンによる削減効果の定量検証</li>
            <li><Check size={16} />現場オペレーターの積付ノウハウをシステムへ反映</li>
            <li><Check size={16} />IBCコード適合性チェックの自動化で属人化リスクを低減</li>
            <li><Check size={16} />配船・バンカリングへの最適化範囲の拡張</li>
          </ul>
        </div>
      </section>
    </main>
  );
}

/* --------------------------------------------------------- compat matrix */
function CompatMatrix() {
  const keys = ["caustic_soda", "sulphuric_acid", "phosphoric_acid", "methanol", "toluene", "xylene", "vegetable_oil"];
  const [sel, setSel] = useState(null);
  const relColor = { reactive: "#ff6b5e", forbidden_same_tank: "#f4b942", forbidden_adjacent: "#7f93c8", compatible: "#2dd4bf" };
  const relLabel = { reactive: "反応性（同一タンク禁止）", forbidden_same_tank: "同一タンク禁止", forbidden_adjacent: "隣接タンク禁止", compatible: "適合" };
  return (
    <div className="matrix-wrap">
      <table className="matrix">
        <thead>
          <tr><th></th>{keys.map((k) => <th key={k}><span>{CARGO[k].ja}</span></th>)}</tr>
        </thead>
        <tbody>
          {keys.map((r) => (
            <tr key={r}>
              <th className="rowh">{CARGO[r].ja}</th>
              {keys.map((c) => {
                if (r === c) return <td key={c} className="diag">—</td>;
                const rel = relation(r, c) || "compatible";
                const active = sel && ((sel.a === r && sel.b === c) || (sel.a === c && sel.b === r));
                return (
                  <td key={c}>
                    <button
                      className={"cell" + (active ? " active" : "")}
                      style={{ background: relColor[rel] }}
                      onClick={() => setSel({ a: r, b: c, rel })}
                      title={`${CARGO[r].ja} × ${CARGO[c].ja}: ${relLabel[rel]}`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="matrix-side">
        <div className="legend2">
          {Object.entries(relLabel).map(([k, v]) => (
            <span key={k}><i style={{ background: relColor[k] }} />{v}</span>
          ))}
        </div>
        {sel && (
          <div className="matrix-read">
            <b>{CARGO[sel.a].ja}</b> × <b>{CARGO[sel.b].ja}</b>
            <div className="mr-rel" style={{ color: relColor[sel.rel] }}>{relLabel[sel.rel]}</div>
            <p>{sel.rel === "compatible"
              ? "この組み合わせは適合性上の制約はありません。"
              : "この組み合わせは同一計画内で制約が適用され、最適化時に自動で回避されます。"}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- FEATURES */
function Features() {
  const mods = [
    [Compass, "OPTIMIZER", "積付シーケンス最適化", "OR-Tools CP-SATによる制約プログラミングで、整数スケール化した目的関数（洗浄＋混載リスク＋滞船）を最小化する荷役順序を導出します。", ["制約ソルバー", "多目的最小化", "数秒で求解"]],
    [FlaskConical, "CHEMISTRY", "適合性マトリクス", "積荷相互および直前貨物との化学的適合性を判定し、反応・汚染の危険な組み合わせを可視化します。", ["相互適合性", "直前貨物判定", "リスクスコア"]],
    [ShieldCheck, "COMPLIANCE", "IBCコード検証", "国際バルクケミカルコードの適合性ルールを制約として組み込み、違反となる計画を生成段階でブロックします。", ["IBCコード", "ハード制約", "監査ログ"]],
    [Droplets, "CLEANING", "洗浄プランナー", "各タンクの洗浄要否・洗浄グレード・所要コストを算出し、不要な洗浄工程を排除します。", ["洗浄要否判定", "グレード最適化", "スロップ管理"]],
    [Scale, "BALLAST", "バラスト／安定性", "喫水・トリム・船体安定性を考慮し、バラスト配分を積付計画と整合させます。", ["喫水/トリム", "安定性考慮", "配分最適化"]],
    [Sparkles, "CLEANLINESS", "タンク清浄度管理", "各タンクの清浄度履歴を追跡し、直前貨物からの残留リスクを次航海の計画に反映します。", ["清浄度履歴", "残留リスク", "次航反映"]],
    [DollarSign, "SAVINGS", "コスト可視化", "洗浄・滞船・コンタミ回避による削減効果を金額で提示し、意思決定の根拠を示します。", ["削減額試算", "比較シナリオ", "根拠の明示"]],
    [Plug, "EXPORT · KC360", "連携・エクスポート", "確定した積付計画をKC360をはじめとする既存システムへデータ出力。EN/JA両対応です。", ["KC360連携", "EN / JA", "What-if比較"]],
  ];
  return (
    <main className="view">
      <div className="fhd wrap">
        <p className="eyebrow">Product Functionality Overview</p>
        <h1>機能一覧 — Functionalities</h1>
        <p>ケミカルタンカーの積付を、化学・コンプライアンス・洗浄・機器制約まで含めて最適化する統合プラットフォーム。1画面で計画・根拠・削減効果を確認できます。</p>
        <a className="btn btn-primary" href="#/demo" style={{ marginTop: 22 }}>実際に最適化してみる <ArrowRight size={16} /></a>
      </div>

      <section className="wrap">
        <div className="pipe">
          {[["INPUT", "貨物・航路・タンク", "積荷、寄港順、直前貨物を登録"],
            ["CONSTRAINTS", "IBC適合性・機器", "規則と物理制約を自動適用"],
            ["SOLVE", "CP-SAT 求解", "総コスト最小の順序を導出"],
            ["OUTPUT", "計画・削減額・KC360", "可視化とデータ出力"]].map(([k, h, p], i, a) => (
            <React.Fragment key={k}>
              <div className="pstep"><div className="k">{k}</div><h4>{h}</h4><p>{p}</p></div>
              {i < a.length - 1 && <ChevronRight className="pipe-arrow" size={18} />}
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="wrap">
        <p className="eyebrow">Interactive · Chemistry</p>
        <h2>適合性マトリクス（クリックで確認）</h2>
        <p className="sec-lede">セルをクリックすると、その2貨物間のIBC適合性関係を確認できます。最適化エンジンはこれらの制約を自動で計画に反映します。</p>
        <CompatMatrix />
      </section>

      <section className="wrap">
        <h2>8つのコアモジュール</h2>
        <p className="sec-lede">それぞれが単独でも価値を持ち、統合することで「計画の質」を最大化します。</p>
        <div className="mgrid">
          {mods.map(([Icon, tag, title, body, tags]) => (
            <div className="mod" key={tag}>
              <div className="mrow"><span className="mic"><Icon size={22} /></span>
                <div><div className="mtag">{tag}</div><h3>{title}</h3></div>
              </div>
              <p>{body}</p>
              <ul>{tags.map((t) => <li key={t}>{t}</li>)}</ul>
            </div>
          ))}
        </div>
        <div className="stack">
          <h3>技術スタック</h3>
          <div className="chips">
            {["FastAPI", "OR-Tools CP-SAT", "SQLAlchemy", "PostgreSQL"].map((c) => <span className="chip" key={c}>{c}</span>)}
            {["React", "TypeScript", "Vite", "Tailwind CSS"].map((c) => <span className="chip alt" key={c}>{c}</span>)}
          </div>
        </div>
      </section>
    </main>
  );
}

/* -------------------------------------------------------------------- DEMO */
const RESIDUE_POOL = ["sulphuric_acid", "methanol", "caustic_soda", "ethanol", "vegetable_oil", "toluene", "xylene", "phosphoric_acid", "palm_oil", "isopropanol"];

function Demo() {
  const [tanks, setTanks] = useState(DEFAULT_TANKS);
  const [parcels, setParcels] = useState(DEFAULT_PARCELS);
  const [result, setResult] = useState(null);
  const [solving, setSolving] = useState(false);

  const byTank = useMemo(() => {
    const m = {};
    if (result && result.status === "optimal")
      result.assignments.forEach((a) => { m[a.tankId] = a; });
    return m;
  }, [result]);

  function run() {
    setSolving(true);
    setResult(null);
    // small delay so the "solving" state is perceptible
    setTimeout(() => {
      setResult(optimize(parcels, tanks));
      setSolving(false);
    }, 260);
  }

  function toggleCargo(key) {
    setResult(null);
    setParcels((prev) => {
      const idx = prev.findIndex((p) => p.cargo === key);
      if (idx >= 0) return prev.filter((p) => p.cargo !== key);
      if (prev.length >= tanks.length) return prev; // cap at tank count
      return [...prev, { cargo: key, vol: 900 }];
    });
  }

  function setVol(key, vol) {
    setResult(null);
    setParcels((prev) => prev.map((p) => (p.cargo === key ? { ...p, vol } : p)));
  }

  function shuffleVoyage() {
    setResult(null);
    const pool = [...RESIDUE_POOL];
    setTanks((prev) => prev.map((t) => {
      const pick = pool.splice(Math.floor(Math.random() * pool.length), 1)[0] || t.last;
      return { ...t, last: pick };
    }));
  }

  const selectedKeys = parcels.map((p) => p.cargo);

  return (
    <main className="view demo">
      <div className="fhd wrap">
        <p className="eyebrow">Live · Constraint Solver</p>
        <h1>積付最適化デモ</h1>
        <p>タンクの直前貨物（残留）と積み込む貨物を選び、<b>最適化を実行</b>してください。ブラウザ上で実際の割当最適化（容量・IBC適合性・隣接・洗浄コスト）が走り、最小コストの積付計画を導出します。</p>
      </div>

      <section className="wrap demo-grid">
        {/* ---- controls ---- */}
        <div className="control">
          <div className="cbox">
            <div className="cbox-h">
              <h3><Ship size={17} /> 船体構成（直前貨物）</h3>
              <button className="mini" onClick={shuffleVoyage}><RefreshCw size={13} /> 別の航海</button>
            </div>
            <p className="hint">各タンクに前航海の残留貨物があります。これが洗浄コストを左右します。</p>
            <div className="tankgrid">
              {tanks.map((t) => {
                const c = CARGO[t.last];
                return (
                  <div className="tcell" key={t.id}>
                    <div className="tcell-id">{t.id}<span>{t.cap.toLocaleString()}m³</span></div>
                    <div className="tcell-res" style={{ background: GROUP_COLOR[c.group] }}>{c.ja}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="cbox">
            <div className="cbox-h"><h3><Beaker size={17} /> 積み込む貨物</h3><span className="cnt">{parcels.length} / {tanks.length}</span></div>
            <p className="hint">クリックで選択・解除。最大 {tanks.length} 品目まで。</p>
            <div className="palette">
              {CARGOES.map((c) => {
                const on = selectedKeys.includes(c.key);
                return (
                  <button key={c.key} className={"pchip" + (on ? " on" : "")}
                    style={on ? { background: GROUP_COLOR[c.group], borderColor: GROUP_COLOR[c.group] } : {}}
                    onClick={() => toggleCargo(c.key)}>
                    {on ? <Check size={13} /> : null}{c.ja}
                  </button>
                );
              })}
            </div>

            {parcels.length > 0 && (
              <div className="vols">
                {parcels.map((p) => {
                  const c = CARGO[p.cargo];
                  const over = p.vol > Math.max(...tanks.map((t) => t.cap));
                  return (
                    <div className="vrow" key={p.cargo}>
                      <span className="vname"><i style={{ background: GROUP_COLOR[c.group] }} />{c.ja}</span>
                      <input type="range" min="400" max="1800" step="50" value={p.vol}
                        onChange={(e) => setVol(p.cargo, Number(e.target.value))} />
                      <span className={"vval" + (over ? " over" : "")}>{p.vol.toLocaleString()}m³</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button className="btn btn-primary run" disabled={solving || parcels.length === 0} onClick={run}>
            {solving ? <><RefreshCw size={16} className="spin" /> 求解中…</> : <><Play size={16} /> 最適化を実行</>}
          </button>
        </div>

        {/* ---- results ---- */}
        <div className="results">
          {!result && !solving && (
            <div className="empty">
              <Waves size={30} />
              <p>左で貨物を選び「最適化を実行」を押すと、<br />制約ソルバーが最小コストの積付計画を導出します。</p>
            </div>
          )}
          {solving && <div className="empty"><RefreshCw size={30} className="spin" /><p>制約充足問題を求解しています…</p></div>}

          {result && result.status === "infeasible" && (
            <div className="infeasible">
              <div className="badge red"><AlertTriangle size={15} /> 実行不可 — 制約を充足できません</div>
              <p className="hint">以下の貨物は、現在の船体構成では適合する割当が存在しません。ソルバーが原因を特定しました。</p>
              <ul className="reasons">
                {result.reasons.map((r) => (
                  <li key={r.cargo}>
                    <b>{CARGO[r.cargo].ja}</b>：
                    {r.cause === "no_capacity" && "容量不足（積載量がすべてのタンク容量を超過）"}
                    {r.cause === "all_transitions_forbidden" && "全タンクの直前貨物と反応性/同一タンク禁止の関係"}
                    {r.cause === "combined_constraints" && "容量・適合性・隣接制約の組み合わせにより割当不可"}
                  </li>
                ))}
              </ul>
              <p className="hint">「別の航海」で残留貨物を変える、積載量を下げる、貨物を入れ替えると解が見つかります。</p>
            </div>
          )}

          {result && result.status === "optimal" && (
            <>
              <div className="rhead">
                <div className="badge green"><Check size={15} /> 最適解</div>
                <span className="solvet">solver: CP-SAT 相当 · {result.solveMs}ms · optimal</span>
              </div>

              <div className="kpis">
                <div className="kpi"><span>総コスト</span><b>{usd(result.optTotal)}</b></div>
                <div className="kpi"><span>総洗浄時間</span><b>{result.totalHours}<em>h</em></b></div>
                <div className="kpi"><span>IBC違反</span><b className="good">0</b></div>
                <div className="kpi"><span>手動割当て比</span><b className="accent">−{result.savingsPct}%</b></div>
              </div>

              <div className="plan">
                {tanks.map((t) => {
                  const a = byTank[t.id];
                  const rc = CARGO[t.last];
                  return (
                    <div className={"prow" + (a ? "" : " idle")} key={t.id}>
                      <div className="ptank">{t.id}</div>
                      <div className="pflow">
                        <span className="res" style={{ background: GROUP_COLOR[rc.group] }}>{rc.ja}</span>
                        <ArrowRight size={14} className="arr" />
                        {a
                          ? <span className="load" style={{ background: GROUP_COLOR[CARGO[a.cargo].group] }}>{CARGO[a.cargo].ja} <em>{a.vol.toLocaleString()}m³</em></span>
                          : <span className="load empty2">空タンク</span>}
                      </div>
                      <div className="pcost">
                        {a ? <>
                          <b>{usd(a.total)}</b>
                          <span className="pdet">
                            洗浄 {usd(a.chem)} · {a.hours}h
                            {a.wallWash ? <span className="tagx">壁面洗浄</span> : null}
                            {a.heating ? <span className="tagx warm"><Flame size={10} /> 加熱</span> : null}
                          </span>
                        </> : <span className="pdet">—</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="compare">
                <div><span>最適化計画</span><b>{usd(result.optTotal)}</b></div>
                <div className="bar">
                  <div className="bar-opt" style={{ width: (100 * result.optTotal / result.naiveTotal) + "%" }} />
                </div>
                <div className="naive"><span>手動割当て（先着順）{result.naiveViolations > 0 ? `・適合性違反 ${result.naiveViolations}件` : ""}</span><b>{usd(result.naiveTotal)}</b></div>
              </div>
              <p className="foothint">※ 洗浄コストは実データ由来のクリーニングマトリクス、コンタミ severity は IBC 関係に基づく想定値です。ブラウザ内で全割当を探索した厳密解です。</p>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

/* -------------------------------------------------------------------- root */
export default function App() {
  const route = useHashRoute();
  const page = route === "/tankwise" ? <Home />
    : route === "/features" ? <Features />
    : route === "/demo" ? <Demo />
    : <CompanyHome />;
  return (
    <div className="app">
      <Nav route={route} />
      {page}
      <footer>
        <div className="wrap foot-in">
          <a className="brand sm" href="#/"><span className="mark"><Target size={13} /></span>Shirushi<span className="brand-ai">.AI</span></a>
          <div className="muted">AIソフトウェア・コンサルティング ｜ 海運・ライブコマース・ギグエコノミー ｜ © 2026 Shirushi.AI</div>
          <div className="foot-links">
            <a href="#/">会社概要</a><a href="#/tankwise">TankWise</a><a href="#/features">機能一覧</a><a href="#/demo">デモ</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
